import React, { useEffect, useRef, useState } from 'react';
import { Card, Empty, Flex, Tabs, Typography, theme } from 'antd';

import useQueryStringParameters from '../../../utils/useQueryStringParameters';
import { hashPageUrl } from '../hashPageUrl';
import {
  buildSearchPerformanceQueryParameters,
  type SearchPerformanceQueryParams,
} from '../SearchPerformanceQueryParameters';
import { getReportingWindow } from '../SearchPerformanceReportingWindow';
import SearchPerformanceRequestError from '../SearchPerformanceRequestError';
import SearchPerformanceTableSkeleton from '../SearchPerformanceTableSkeleton';
import { statusFilterScopeKey, type StatusCodeClass } from '../StatusCodeClass';
import { useUrlInspectionAccess } from '../UrlInspection';
import useSearchPerformanceProperty from '../useSearchPerformanceProperty';
import useTrackSearchPerformanceEvent from '../useTrackSearchPerformanceEvent';
import PagesInspectDrawer from './PagesInspectDrawer';
import { PAGES_PAGE_SIZE } from './PagesPaging';
import { PAGES_SECTION_ID } from './PagesSectionLink';
import { SORT_FIELD_API_SORT_BY, type PagesSort } from './PagesSort';
import { type PagesJoinedRow } from './PagesStore';
import PagesTable, { type PagesRow, type PagesTableChange } from './PagesTable';
import { PAGES_TABS, isPagesTabKey, pagesTabDefinition, type PagesTabKey } from './PagesTabs';
import PagesToolbar from './PagesToolbar';
import { usePagesLoadTelemetry } from './usePagesLoadTelemetry';
import { usePagesRows } from './usePagesRows';
import { usePagesUrlSearch } from './usePagesUrlSearch';
import { useTrackPagesTabViewed } from './useTrackPagesTabViewed';

const PAGES_SECTION_STRINGS = {
  title: 'Pages',
  loadFailed: 'Unable to load the pages table.',
  decorationFailed: 'Unable to load part of the pages table — some columns show "—" until it is retried.',
  filterEmpty: {
    noMatch: 'No pages match your filter.',
    suggestion: 'Try a different search term or clear the filter.',
  },
};

function toTableRow(row: PagesJoinedRow): PagesRow {
  return {
    key: row.pageId,
    pageId: row.pageId,
    url: row.url,
    impressions: row.impressions,
    clicks: row.clicks,
    ctrPct: row.ctrPct,
    lastStatusCode: row.lastStatusCode,
    lastCrawledAt: row.lastCrawledAt,
    metricState: row.metricState,
    coverageState: row.coverageState,
    inspectedAt: row.inspectedAt,
  };
}

/**
 * The Pages URL-level drilldown. Tab and URL search live in the URL (deep links,
 * reload); sort, the status filter and the page stay local.
 */
function PagesSection() {
  const { token } = theme.useToken();
  const trackEvent = useTrackSearchPerformanceEvent();
  const hasUrlInspectionAccess = useUrlInspectionAccess();

  const { selectedProperty, isLoading: isPropertyLoading } = useSearchPerformanceProperty();
  const { dateStart, dateEnd } = getReportingWindow();
  const { queryStrings, updateMultipleParameters } = useQueryStringParameters<SearchPerformanceQueryParams>(
    buildSearchPerformanceQueryParameters
  );

  const tab: PagesTabKey = isPagesTabKey(queryStrings.tab) ? queryStrings.tab : 'top';
  const search = usePagesUrlSearch();

  const [sortOverride, setSortOverride] = useState<PagesSort | null>(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<StatusCodeClass[] | undefined>(undefined);
  const [inspectedRow, setInspectedRow] = useState<PagesRow | null>(null);

  const propertyUrl = selectedProperty?.propertyUrl;

  // A sort or column filter belongs to a tab (impressions are uniformly 0/— on
  // two of them) and to a property. Reset during render so the first request
  // of the new scope is already clean.
  const sortScopeKey = `${propertyUrl}|${tab}`;
  const [sortScope, setSortScope] = useState(sortScopeKey);
  if (sortScope !== sortScopeKey) {
    setSortScope(sortScopeKey);
    setSortOverride(null);
    setStatusFilter(undefined);
    setInspectedRow(null);
  }
  const activeSortOverride = sortScope !== sortScopeKey ? null : sortOverride;
  const activeStatusFilter = sortScope !== sortScopeKey ? undefined : statusFilter;

  const tabDefinition = pagesTabDefinition(tab);
  const sort =
    activeSortOverride && tabDefinition.sortableFields.includes(activeSortOverride.field)
      ? activeSortOverride
      : tabDefinition.defaultSort;

  const pagesFilterKey = [search.committed, statusFilterScopeKey(activeStatusFilter)];
  // Page 5 of one filter combination is meaningless for another — a stale
  // offset against a smaller total renders an empty page.
  const pageScopeKey = JSON.stringify([sortScopeKey, ...pagesFilterKey]);
  const [pageScope, setPageScope] = useState(pageScopeKey);
  if (pageScope !== pageScopeKey) {
    setPageScope(pageScopeKey);
    setPage(1);
  }
  const queryPage = pageScope !== pageScopeKey ? 1 : page;
  const pagesInteractionKey = [sort.field, sort.order, queryPage, ...pagesFilterKey];

  const { rows, total, hasRows, isUninitialized, isFetching, isError, isDecorationError, refetch, loadCycle } =
    usePagesRows({
      property: propertyUrl,
      dateStart,
      dateEnd,
      tab,
      sort,
      page: queryPage,
      urlContains: search.committed === '' ? undefined : search.committed,
      statusFilter: activeStatusFilter,
    });

  const { trackRetryClicked } = usePagesLoadTelemetry({
    property: propertyUrl,
    reportingWindow: { dateStart, dateEnd },
    tab,
    interactionQueryArgs: pagesInteractionKey,
    loadCycle,
  });

  const isPending = isPropertyLoading || (!!selectedProperty && (isUninitialized || (isFetching && !hasRows)));

  const tableRows = rows.map(toTableRow);
  // The drawer follows the live joined row while it is on screen — a
  // decoration landing after the click fills its other half — and keeps the
  // clicked snapshot once the row has paged away.
  const inspectRow =
    inspectedRow === null ? null : tableRows.find((row) => row.pageId === inspectedRow.pageId) ?? inspectedRow;

  useTrackPagesTabViewed(tab, selectedProperty);

  // Once per typed search, not per keystroke; a Review-typed search is its own event.
  const isTypedSearchActive = search.committed !== '' && search.wasTyped;
  const countedTypedSearchRef = useRef(false);
  useEffect(() => {
    if (isTypedSearchActive && !countedTypedSearchRef.current) {
      trackEvent('Search Performance: Pages URL Searched', { tab });
    }
    countedTypedSearchRef.current = isTypedSearchActive;
  }, [isTypedSearchActive, tab, trackEvent]);

  const handleRetry = () => {
    trackRetryClicked();
    refetch();
  };

  const handleTabChange = (nextTab: string) => {
    if (!isPagesTabKey(nextTab)) return;
    updateMultipleParameters({ tab: nextTab });
  };

  const handleTableChange = (change: PagesTableChange) => {
    if (change.action === 'sort' && change.sort) {
      trackEvent('Search Performance: Pages Sort Changed', {
        tab,
        column: SORT_FIELD_API_SORT_BY[change.sort.field],
        direction: change.sort.order,
      });
    }
    if (change.action === 'filter') {
      trackEvent('Search Performance: Pages Filter Changed', {
        filter_name: 'http_status',
        // Empty selection reads as "all"; a class list joins in canonical order.
        old_value: statusFilterScopeKey(activeStatusFilter) || 'all',
        new_value: statusFilterScopeKey(change.statusFilter) || 'all',
      });
    }
    setSortOverride(change.sort ?? null);
    setStatusFilter(change.statusFilter);
    setPage(change.page);
  };

  const handleClearFilters = () => {
    trackEvent('Search Performance: Pages Clear Filters Clicked', { tab });
    search.clear();
    setStatusFilter(undefined);
    setSortOverride(null);
    setPage(1);
  };

  // Filter-active zeros are "no match", not the tab's factual empty copy.
  const hasActiveFilters = search.committed !== '' || (activeStatusFilter?.length ?? 0) > 0;
  const emptyText = hasActiveFilters ? (
    <Empty
      description={
        <span>
          {PAGES_SECTION_STRINGS.filterEmpty.noMatch}
          <br />
          <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            {PAGES_SECTION_STRINGS.filterEmpty.suggestion}
          </Typography.Text>
        </span>
      }
    />
  ) : (
    <Empty description={tabDefinition.emptyText} />
  );

  return (
    <Flex id={PAGES_SECTION_ID} vertical gap={token.marginXS}>
      <Typography.Title
        level={3}
        style={{ margin: 0, fontSize: token.fontSizeHeading4, fontWeight: token.fontWeightStrong }}
      >
        {PAGES_SECTION_STRINGS.title}
      </Typography.Title>

      {isError && <SearchPerformanceRequestError message={PAGES_SECTION_STRINGS.loadFailed} onRetry={handleRetry} />}
      {isDecorationError && (
        <SearchPerformanceRequestError message={PAGES_SECTION_STRINGS.decorationFailed} onRetry={handleRetry} />
      )}

      {(isPending || hasRows) && (
        <Card>
          <Flex vertical gap={token.marginXS}>
            <PagesToolbar
              searchText={search.inputValue}
              onSearchChange={search.setInputValue}
              onRefresh={() => refetch()}
              onClearFilters={handleClearFilters}
            />

            <Tabs
              activeKey={tab}
              onChange={handleTabChange}
              items={PAGES_TABS.map(({ key, label }) => ({ key, label }))}
            />

            {isPending ? (
              <SearchPerformanceTableSkeleton rowCount={PAGES_PAGE_SIZE} />
            ) : (
              <PagesTable
                rows={tableRows}
                loading={isFetching}
                page={queryPage}
                total={total}
                sort={sort}
                sortableFields={tabDefinition.sortableFields}
                unsortableReason={tabDefinition.unsortableReason}
                statusFilter={activeStatusFilter}
                emptyText={emptyText}
                onChange={handleTableChange}
                onInspectRow={setInspectedRow}
                showInspection={hasUrlInspectionAccess}
                onUrlClick={(url) =>
                  trackEvent('Search Performance: Page URL Clicked', { tab, url_hash: hashPageUrl(url) })
                }
              />
            )}
          </Flex>
        </Card>
      )}

      <PagesInspectDrawer
        row={inspectRow}
        onClose={() => setInspectedRow(null)}
        showInspection={hasUrlInspectionAccess}
      />
    </Flex>
  );
}

export default PagesSection;
